import { Injectable } from '@angular/core';
import { UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { AuthenticationService, BannerMessageError, FormControlConfigurationOptions, FormControlOptions, FormDataContainer, TranslationService } from '@ascent/shared';
import { EshavaFormControl, EshavaFormControlConfiguration, EshavaFormControlType, EshavaFormControlValidationConfiguration, EshavaFormControlValidationRule } from 'eshava-controls';
import { ValidatorService } from './validator.service';
import { DateTime } from 'luxon';

@Injectable({
  providedIn: 'root'
})
export class FormularService {

  constructor(
    private _authenticationService: AuthenticationService,
    private _validatorService: ValidatorService,
    private _translationService: TranslationService
  ) { }

  public createFormConfiguration(options: FormControlConfigurationOptions): EshavaFormControlConfiguration {

    return {
      id: DateTime.now().toISO(),
      formGroup: new UntypedFormGroup({}),
      isReadonly: !!options.isReadonly,
      isDisabled: !!options.isDisabled,
      disableStateChanged$: options.subjectDisableStateChanged.asObservable(),
      readonlyStateChanged$: options.subjectReadonlyStateChanged.asObservable(),
      subjectValidateControl: new Subject<EshavaFormControl>()
    };
  }

  public createFormControls(options: FormControlOptions): EshavaFormControl[] {
    const hasFilter = !!options.controlNameFilter && options.controlNameFilter.length > 0;

    if (hasFilter && options.invertControlNameFilter) {
      options.controlValidationConfigurations = options.controlValidationConfigurations.filter(v => options.controlNameFilter!.indexOf(v.propertyName) === -1);
    } else if (hasFilter && !options.invertControlNameFilter) {
      options.controlValidationConfigurations = options.controlValidationConfigurations.filter(v => options.controlNameFilter!.indexOf(v.propertyName) > -1);
    }

    const controlList = this.generateControlList(options.controlValidationConfigurations, options.formData);
    this.applyValidationRulesToControls(controlList);

    return controlList;
  }

  public collectFormularErrors(formGroup: UntypedFormGroup, tranlationGroup: string, onlyControls: string[] = []): BannerMessageError[] {
    if (formGroup.valid) {
      return [];
    }

    const fields: BannerMessageError[] = [];
    Object.getOwnPropertyNames(formGroup.controls).forEach(p => {

      const control = formGroup.controls[p] as unknown as EshavaFormControl;
      if (!!onlyControls
        && onlyControls.length > 0
        && onlyControls.indexOf(control.validationConfig.propertyName) === -1) {
        return;
      }

      const errors = formGroup.controls[p].errors;
      if (errors) {
        fields.push({
          name: control.validationConfig.propertyName,
          translation: this._translationService.translate(control.validationConfig.propertyName, tranlationGroup),
          errors: errors
        });
      }
    });

    return fields;
  }

  public getChangedPropertiesOnly<T extends { [key: string]: any }>(item: T, itemBackup: T): T {
    const newItem: T = {} as T;

    // process backup object
    this.processBackupItem<T>(itemBackup, item, newItem);

    // process current object
    this.processItem<T>(item, itemBackup, newItem);

    return newItem;
  }

  private processItem<T extends { [key: string]: any }>(item: T, itemBackup: T, newItem: T): void {
    Object.keys(item).forEach(key => {
      const propertyValue = item[key];
      const propertyBackupValue = itemBackup[key];
      const isObjectArray = this.isObjectArray(propertyValue, propertyBackupValue);

      if (!isObjectArray && propertyValue !== propertyBackupValue && propertyValue !== undefined) {
        // primitve data type property or primitive data type array property
        if (!this.checkForPrimitiveValueArrays(newItem, key, propertyValue, propertyBackupValue)) {
          this.checkForObject(newItem, key, propertyValue, propertyBackupValue);
        }

      } else if (isObjectArray && Array.isArray(propertyValue)) {

        if ((Array.isArray(propertyBackupValue) && propertyBackupValue.length > 0)) {
          const newPropertyValues: any[] = this.getChangesForArray(propertyValue, propertyBackupValue);
          if (newPropertyValues.length > 0) {
            Reflect.set(newItem, key, newPropertyValues);
          }
        } else {
          const newPropertyValues: any[] = this.getChangesForArray(propertyValue, []);
          if (newPropertyValues.length > 0) {
            Reflect.set(newItem, key, newPropertyValues);
          }
        }
      }
    });
  }

  private processBackupItem<T extends { [key: string]: any }>(itemBackup: T, item: T, newItem: T): void {
    Object.keys(itemBackup).forEach(key => {
      const propertyValue = item[key];
      const propertyBackupValue = itemBackup[key];
      const isObjectArray = this.isObjectArray(propertyValue, propertyBackupValue);

      if (!isObjectArray && propertyBackupValue !== propertyValue && propertyValue !== undefined) {
        // primitve data type property or primitive data type array property
        if (!this.checkForPrimitiveValueArrays(newItem, key, propertyValue, propertyBackupValue)) {
          this.checkForObject(newItem, key, propertyValue, propertyBackupValue);
        }

      } else if (isObjectArray && (propertyValue?.length ?? 0) === 0 && (propertyBackupValue?.length ?? 0) > 0) {

        // new array is empty, backup array not, so add all entries as deleted
        const newPropertyValues: any[] = [];
        Reflect.set(newItem, key, newPropertyValues);
        for (const arrayValue of propertyBackupValue) {
          if (arrayValue.id) {
            const clone = { id: arrayValue.id, remove: true };
            newPropertyValues.push(clone);
          }
        }
      }
    });
  }

  private checkForPrimitiveValueArrays<T extends { [key: string]: any }>(newItem: T, key: string, propertyValue: any, propertyBackupValue: any): boolean {
    const isPrimityValueArray = Array.isArray(propertyValue);
    const isPrimityBackupValueArray = Array.isArray(propertyBackupValue);

    if ((isPrimityValueArray && isPrimityBackupValueArray)
      || (isPrimityValueArray && (propertyBackupValue === undefined || propertyBackupValue === null))
      || (isPrimityBackupValueArray && (propertyValue === undefined || propertyValue === null))) {

      const propertyValueArray = propertyValue as any[] || [];
      const propertyBackupValueArray = propertyBackupValue as any[] || [];
      if (propertyValueArray.length > 0 || propertyBackupValueArray.length > 0) {
        Reflect.set(newItem, key, propertyValue);
      }

      return true;
    }

    return false;
  }

  private checkForObject<T extends { [key: string]: any }>(newItem: T, key: string, propertyValue: any, propertyBackupValue: any): void {
    if ((typeof propertyValue === 'object' && propertyValue !== null && propertyValue !== undefined)
      || (typeof propertyBackupValue === 'object' && propertyBackupValue !== null && propertyBackupValue !== undefined)) {

      const objectChanges = this.getChangedPropertiesOnly(propertyBackupValue, propertyValue);
      if (Object.keys(objectChanges).length > 0) {
        Reflect.set(newItem, key, objectChanges);
      }


    } else {
      Reflect.set(newItem, key, propertyValue);
    }
  }

  private getChangesForArray(propertyValue: any[], propertyBackupValue: any[]): any[] {
    const newPropertyValues: any[] = [];
    const hasBackupValues = propertyBackupValue?.length > 0;

    for (const arrayValue of propertyValue) {
      if (arrayValue.id) {

        const arrayBackupValue = hasBackupValues
          ? propertyBackupValue.find(abv => abv.id === arrayValue.id)
          : undefined;
        if (arrayBackupValue) {
          const clone = this.getChangedPropertiesOnly(arrayValue, arrayBackupValue);

          if (Object.keys(clone).length > 0) {
            clone.id = arrayValue.id;
            newPropertyValues.push(clone);
          }
        } else {
          const clone = JSON.parse(JSON.stringify(arrayValue));
          clone.add = true;
          newPropertyValues.push(clone);
        }
      } else {
        const clone = JSON.parse(JSON.stringify(arrayValue));
        clone.add = true;
        newPropertyValues.push(clone);
      }
    }

    if (hasBackupValues) {
      for (const arrayBackupValue of propertyBackupValue) {
        const arrayValue = propertyValue.find(av => av.id === arrayBackupValue.id);
        if (!arrayValue) {
          const clone = { id: arrayBackupValue.id, remove: true };
          newPropertyValues.push(clone);
        }
      }
    }

    return newPropertyValues;
  }

  private isObjectArray(propertyValue: any, propertyBackupValue: any): boolean {
    const isArrayPropertyValue = Array.isArray(propertyValue);
    const isArrayPropertyBackupValue = Array.isArray(propertyBackupValue);

    if (!isArrayPropertyValue && !isArrayPropertyBackupValue) {
      return false;
    }

    if (isArrayPropertyValue && (propertyValue as any[]).length > 0) {
      const value = (propertyValue as any[])[0];

      return typeof value === 'object';
    }

    if (isArrayPropertyBackupValue && (propertyBackupValue as any[]).length > 0) {
      const value = (propertyBackupValue as any[])[0];

      return typeof value === 'object';
    }

    return false;
  }

  private generateControlList(validationConfigs: EshavaFormControlValidationConfiguration[], form: FormDataContainer): EshavaFormControl[] {
    const controlList: EshavaFormControl[] = [];
    const culture = this._authenticationService.getUserCulture();

    for (const validationConfig of validationConfigs) {
      if (validationConfig.rules === null || validationConfig.rules === undefined) {
        continue;
      }

      const jsonName = validationConfig.jsonName;
      const convertToNumber = validationConfig.dataType === EshavaFormControlType.number || (validationConfig.dataType === EshavaFormControlType.select && validationConfig.rules.filter(r => r.rule === 'Number').length === 1);
      const transformNumber = validationConfig.dataType === EshavaFormControlType.number && culture === 'de-DE';
      const control: EshavaFormControl = new EshavaFormControl('', []);
      control.culture = culture;
      control.validationConfig = validationConfig;
      control.setValue(form.data[jsonName]);
      control.valueChanges.subscribe((value: any) => {
        value = value !== true && value !== false
          ? value?.toString()
          : value;

        if (convertToNumber && (!!value || value === '0')) {
          if (transformNumber) {
            form.data[jsonName] = value === '0' ? 0 : parseFloat((value || '').toString().replace(',', '.'));
          } else {
            form.data[jsonName] = parseInt(value, 10);
          }
        } else if (convertToNumber && !value && value !== '0') {
          form.data[jsonName] = null;
        } else {
          form.data[jsonName] = value;
        }
      });

      control.reloadFromSource.subscribe(_ => control.setValue(form.data[jsonName]));
      controlList.push(control);
    }

    return controlList;
  }

  private applyValidationRulesToControls(controlList: EshavaFormControl[]): void {

    for (const control of controlList) {

      const validationDefinitions: ValidatorFn[] = [];

      const equalRules: EshavaFormControlValidationRule[] = control.validationConfig.rules.filter(r => r.rule === 'EqualsTo');
      const notEqualRules: EshavaFormControlValidationRule[] = control.validationConfig.rules.filter(r => r.rule === 'NotEqualsTo');

      if (equalRules.length > 0) {
        validationDefinitions.push(this._validatorService.equalTo(equalRules.map(r => this.getControl(controlList, r.propertyName!))));
      }

      if (notEqualRules.length > 0) {
        validationDefinitions.push(this._validatorService.notEqualTo(notEqualRules.map(r => this.getControl(controlList, r.propertyName!)), notEqualRules[0].defaultValue || ''));
      }

      for (const rule of control.validationConfig.rules) {

        switch (rule.rule) {
          case 'Required':
            validationDefinitions.push(Validators.required);
            break;
          case 'Email':
            validationDefinitions.push(Validators.email);
            break;
          case 'Url':
            validationDefinitions.push(this._validatorService.url());
            break;
          case 'Guid':
            validationDefinitions.push(this._validatorService.guid());
            break;
          case 'MinLength':
            validationDefinitions.push(Validators.minLength(rule.value!));
            break;
          case 'Range':
            validationDefinitions.push(this._validatorService.range(rule));
            break;
          case 'RangeFrom':
            validationDefinitions.push(this._validatorService.rangeFrom(this.getControl(controlList, rule.propertyNameFrom!), rule.propertyNameFromAllowNull || false, false));
            break;
          case 'RangeTo':
            validationDefinitions.push(this._validatorService.rangeFrom(this.getControl(controlList, rule.propertyNameTo!), rule.propertyNameToAllowNull || false, true));
            break;
          case 'RangeBetween':
            {
              const from = this.getControl(controlList, rule.propertyNameFrom!);
              const to = this.getControl(controlList, rule.propertyNameTo!);
              validationDefinitions.push(this._validatorService.rangeBetween(from, to));
              from.validationConfig.rules.push({ rule: 'Custom' });
              to.validationConfig.rules.push({ rule: 'Custom' });
              control.validationConfig.rules.push({ rule: 'Custom' });
            }
            break;
          case 'EqualsTo':
            break;
          case 'NotEqualsTo':
            break;
          case 'DecimalPlaces':
            validationDefinitions.push(this._validatorService.decimals(rule.value!));
            break;
          case 'Number':
            validationDefinitions.push(this._validatorService.number());
            break;
          case 'Password':
            control.isPassword = true;
            break;
          case 'ReadOnly':
            control.isReadonly = true;
            control.isDisabledStatusChangeForReadonlyAndDisabled = true;
            break;
        }
      }
      control.setValidators(validationDefinitions);
    }
  }

  private getControl(controlList: EshavaFormControl[], propertyName: string): EshavaFormControl {
    return controlList.find(c => c.validationConfig.propertyName === propertyName)!;
  }
}
